import React, { createContext, useContext, useLayoutEffect, useMemo, useRef } from 'react';

export interface InteractionScope {
    isActive: () => boolean;
    subscribe: (listener: () => void) => () => void;
}

const Context = createContext<InteractionScope | null>(null);

/** Stable activity boundary: visibility changes need not rerender every row. */
export function InteractionScopeProvider({ active, children }: { active: boolean; children: React.ReactNode }) {
    const state = useRef({ active, listeners: new Set<() => void>() });
    state.current.active = active;
    const scope = useMemo<InteractionScope>(() => ({
        isActive: () => state.current.active,
        subscribe: listener => {
            state.current.listeners.add(listener);
            return () => { state.current.listeners.delete(listener); };
        },
    }), []);

    useLayoutEffect(() => {
        state.current.active = active;
        state.current.listeners.forEach(listener => listener());
    }, [active]);
    useLayoutEffect(() => () => {
        state.current.active = false;
        state.current.listeners.forEach(listener => listener());
        state.current.listeners.clear();
    }, []);

    return <Context.Provider value={scope}>{children}</Context.Provider>;
}

export const useInteractionScope = () => useContext(Context);
